import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageTitle } from "@/components/ui/PageTitle";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SalaryDeductionsTab } from "@/components/counting/tabs/SalaryDeductionsTab";
import { useDiscrepancyManagement } from "@/hooks/useDiscrepancyManagement";
import { DiscrepancyItem } from "@/components/counting/discrepancy/DiscrepancyItem";
import { JustificationDialog } from "@/components/counting/discrepancy/JustificationDialog";
import { useState } from "react";
import { Discrepancy } from "@/types/discrepancy";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Contract } from "@/types/contracts";
import { Button } from "@/components/ui/button";
import { AddContractDialog } from "@/components/staff/contracts/AddContractDialog";
import { ContractTerms } from "@/components/staff/contracts/ContractTerms";
import { IncidentsTab } from "@/components/counting/tabs/IncidentsTab";
import { HistoryDifferencesTab } from "@/components/counting/tabs/HistoryDifferencesTab";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, HelpCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
const Staff = () => {
  const {
    discrepancies,
    handleStatusUpdate
  } = useDiscrepancyManagement();
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState<Discrepancy | null>(null);
  const [isJustificationDialogOpen, setIsJustificationDialogOpen] = useState(false);
  const [isAddContractDialogOpen, setIsAddContractDialogOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("discrepancies");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const isMobile = useIsMobile();
  const handleJustifiedClick = (discrepancy: Discrepancy) => {
    setSelectedDiscrepancy(discrepancy);
    setIsJustificationDialogOpen(true);
  };
  const pendingDiscrepancies = discrepancies.filter(d => d.status === "pending");
  const {
    data: contracts,
    isLoading: isLoadingContracts,
    refetch: refetchContracts
  } = useQuery({
    queryKey: ['employee-contracts'],
    queryFn: async () => {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return [];
      const {
        data,
        error
      } = await supabase.from('employee_contracts').select('*').eq('owner_id', user.id);
      if (error) throw error;
      return data as Contract[];
    }
  });
  const handleTabChange = (value: string) => {
    setSelectedTab(value);
    if (isMobile) {
      setIsMenuOpen(false);
    }
  };
  const renderDiscrepanciesTab = () => <Card className="p-4 md:p-6 w-full overflow-hidden px-0 py-0">
      <div className="space-y-4">
        {pendingDiscrepancies.map(discrepancy => <DiscrepancyItem key={discrepancy.id} discrepancy={discrepancy} onStatusUpdate={handleStatusUpdate} onJustifiedClick={handleJustifiedClick} />)}
        {pendingDiscrepancies.length === 0 && <div className="text-center text-gray-500 py-4">
            Aucune différence en attente
          </div>}
      </div>
    </Card>;
  const renderContractsTab = () => <Card className="p-4 md:p-6 w-full overflow-hidden">
      <div className="flex items-center gap-2 mb-6">
        <h3 className="text-lg font-semibold">Liste des contrats</h3>
        <Button variant="ghost" size="sm" onClick={() => setIsHelpDialogOpen(true)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <HelpCircle className="h-4 w-4" />
          <span>Aide</span>
        </Button>
      </div>
      
      {isLoadingContracts ? <div>Chargement des contrats...</div> : <div className="space-y-4">
          <Button onClick={() => setIsAddContractDialogOpen(true)} className="w-full md:w-auto">
            Nouveau contrat
          </Button>
          
          {contracts && contracts.length > 0 ? contracts.map(contract => <Card key={contract.id} className="p-4 overflow-hidden">
                <div className="space-y-4">
                  <h4 className="font-medium break-words">
                    Contrat de {contract.employee_name}
                  </h4>
                  
                  <ContractTerms contract={contract} />

                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 text-sm">
                    <div className="text-gray-500">
                      Créé le {new Date(contract.contract_date).toLocaleDateString()}
                    </div>
                    <div className={`font-medium ${contract.status === 'signed' ? 'text-green-600' : 'text-yellow-600'}`}>
                      {contract.status === 'signed' ? 'Signé' : 'En attente de signature'}
                    </div>
                  </div>
                </div>
              </Card>) : <div className="text-center text-gray-500 py-4">
              Aucun contrat trouvé. Créez votre premier contrat en cliquant sur "Nouveau contrat".
            </div>}
        </div>}
    </Card>;
  const renderTabContent = () => {
    switch (selectedTab) {
      case "discrepancies":
        return renderDiscrepanciesTab();
      case "history":
        return <HistoryDifferencesTab />;
      case "deductions":
        return <SalaryDeductionsTab showActions={true} />;
      case "incidents":
        return <IncidentsTab />;
      case "contracts":
        return renderContractsTab();
      default:
        return null;
    }
  };
  return <DashboardLayout userType="owner">
      <div className="container mx-auto p-4 space-y-6">
        <PageTitle title="Gestion du personnel" subtitle="Gérez vos employés et les retenues sur salaire" />
        
        {isMobile ? <div className="space-y-4 w-full">
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="w-full flex justify-between items-center">
                  <span className="truncate">
                    {selectedTab === "discrepancies" && "Différences en attente"}
                    {selectedTab === "history" && "Historique"}
                    {selectedTab === "deductions" && "Retenues sur salaire"}
                    {selectedTab === "incidents" && "Incidents signalés"}
                    {selectedTab === "contracts" && "Mes contrats"}
                  </span>
                  <Menu className="h-4 w-4 flex-shrink-0" />
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom" className="h-[50vh]">
                <div className="flex flex-col space-y-2 mt-4">
                  <Button variant={selectedTab === "discrepancies" ? "default" : "ghost"} onClick={() => handleTabChange("discrepancies")} className="w-full justify-start">
                    Différences en attente
                  </Button>
                  <Button variant={selectedTab === "history" ? "default" : "ghost"} onClick={() => handleTabChange("history")} className="w-full justify-start">
                    Historique
                  </Button>
                  <Button variant={selectedTab === "deductions" ? "default" : "ghost"} onClick={() => handleTabChange("deductions")} className="w-full justify-start">
                    Retenues sur salaire
                  </Button>
                  <Button variant={selectedTab === "incidents" ? "default" : "ghost"} onClick={() => handleTabChange("incidents")} className="w-full justify-start">
                    Incidents signalés
                  </Button>
                  <Button variant={selectedTab === "contracts" ? "default" : "ghost"} onClick={() => handleTabChange("contracts")} className="w-full justify-start">
                    Mes contrats
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <div className="w-full">
              {renderTabContent()}
            </div>
          </div> : <Tabs value={selectedTab} onValueChange={handleTabChange} className="space-y-4 w-full">
            <TabsList className="w-full overflow-x-auto flex justify-start md:justify-center">
              <TabsTrigger value="discrepancies">Différences en attente</TabsTrigger>
              <TabsTrigger value="history">Historique</TabsTrigger>
              <TabsTrigger value="deductions">Retenues sur salaire</TabsTrigger>
              <TabsTrigger value="incidents">Incidents signalés</TabsTrigger>
              <TabsTrigger value="contracts">Mes contrats</TabsTrigger>
            </TabsList>

            <TabsContent value="discrepancies" className="mt-6">
              {renderDiscrepanciesTab()}
            </TabsContent>

            <TabsContent value="history" className="mt-6">
              <HistoryDifferencesTab />
            </TabsContent>

            <TabsContent value="deductions" className="mt-6">
              <SalaryDeductionsTab showActions={true} />
            </TabsContent>

            <TabsContent value="incidents" className="mt-6">
              <IncidentsTab />
            </TabsContent>

            <TabsContent value="contracts" className="mt-6">
              {renderContractsTab()}
            </TabsContent>
          </Tabs>}

        <Dialog open={isHelpDialogOpen} onOpenChange={setIsHelpDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Guide de gestion des contrats d'employés</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">Pourquoi créer un contrat ?</h4>
                <p>La création d'un contrat est une étape essentielle avant toute collaboration avec un employé. Il permet de :</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Établir clairement les conditions de travail et les responsabilités</li>
                  <li>Protéger légalement l'employeur et l'employé</li>
                  <li>Éviter les malentendus sur les retenues de salaire en cas de différences</li>
                  <li>Définir les procédures en cas d'écarts d'inventaire</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Comment créer un contrat ?</h4>
                <ol className="list-decimal ml-6 space-y-1">
                  <li>Cliquez sur le bouton "Nouveau contrat"</li>
                  <li>Remplissez le nom de l'employé</li>
                  <li>Sélectionnez les termes du contrat applicables</li>
                  <li>Envoyez le contrat à l'employé pour signature</li>
                </ol>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Termes du contrat</h4>
                <ul className="list-disc ml-6 space-y-1">
                  <li><span className="font-medium">Retenues sur salaire :</span> Autorise les retenues en cas de différences non justifiées</li>
                  <li><span className="font-medium">Écarts d'inventaire :</span> Définit la responsabilité en cas de manquements</li>
                  <li><span className="font-medium">Différences de comptage :</span> Établit les procédures en cas d'erreurs répétées</li>
                  <li><span className="font-medium">Conditions de résiliation :</span> Précise les motifs de fin de contrat</li>
                </ul>
              </div>

              <div className="bg-yellow-50 p-4 rounded-md">
                <p className="font-medium text-yellow-800">Important :</p>
                <p className="text-yellow-700">Un contrat signé est nécessaire avant toute application de retenues sur salaire ou de sanctions liées aux différences de comptage.</p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setIsHelpDialogOpen(false)}>J'ai compris</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <JustificationDialog open={isJustificationDialogOpen} onOpenChange={setIsJustificationDialogOpen} discrepancy={selectedDiscrepancy} onSubmit={explanation => {
        if (selectedDiscrepancy) {
          handleStatusUpdate(selectedDiscrepancy.id, "justified", explanation);
        }
        setIsJustificationDialogOpen(false);
      }} />

        <AddContractDialog open={isAddContractDialogOpen} onOpenChange={setIsAddContractDialogOpen} onSuccess={() => {
        setIsAddContractDialogOpen(false);
        refetchContracts();
      }} />
      </div>
    </DashboardLayout>;
};
export default Staff;